/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module DataGridModule
 */

import {
    AfterViewInit,
    Component,
    DoCheck,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';

import {
    Observable,
    Subject,
} from 'rxjs';

import {
    debounce,
    isString,
} from 'underscore';

import { L10nService } from '@vmw/ngx-vip';

import {
    ClrDatagridComparatorInterface,
    ClrDatagridStateInterface,
} from '@clr/angular';

import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { ConnectedPosition } from '@angular/cdk/overlay';
import {
    IAviDropdownButtonAction,
    TOP_LEFT_ALIGNED_CONNECTED_POSITION,
} from 'ng/shared/components/avi-dropdown-button';

import * as globalL10n from 'global-l10n';

import {
    AviDataGridFieldVisibility,
    IAviDataGridConfig,
    IAviDataGridConfigField,
    IAviDataGridLayout,
    IAviDataGridMultipleaction,
    IAviDataGridRow,
    IAviDataGridSingleaction,
    TAviDataGridColumnPresenceMap,
} from '../avi-data-grid/avi-data-grid.types';
import './avi-data-grid-base.component.less';

import * as l10n from './avi-data-grid-base.l10n';

const { ...globalL10nKeys } = globalL10n;
const { ENGLISH: dictionary, ...l10nKeys } = l10n;

const defaultLayout: IAviDataGridLayout = {
    showFooter: true,
};

/**
 * Calls either an action on a list of rows (multipleaction) or a single row (singleaction). We use
 * a try-catch here because if we were to think about AviDataGrid as a component from a third-party
 * library, we wouldn't want to rely on action.onClick being error-free and have the component break
 * down if it fails.
 */
const callAction = (
    action: IAviDataGridSingleaction | IAviDataGridMultipleaction,
    rows: IAviDataGridRow | IAviDataGridRow[],
): void => {
    try {
        action.onClick(rows);
    } catch (error) {
        throw new Error(`action failed: ${error}`);
    }
};

/**
 * @description
 *
 *     Grid component, created as a wrapper around Clarity's Datagrid component.
 *
 *     Should only be utilized for extension purposes and not be exposed for any direct use.
 *     This component serves as a base to provide essential functionalities to build grid components
 *     with more specific purpose such as avi-data-grid and avi-collection-data-grid.
 *
 *     When using custom templates for cells, we need to use templateRefs as creating a template
 *     from a string is not supported in Angular. This means that the parent component needs to
 *     create a templateRef variable, then pass it into config.fields in ngAfterViewInit. The outlet
 *     context contains the row and the index.
 *
 *     Please refer to avi-data-grid.stories.ts for an example.
 *
 *     Note: Clarity's datagrid has an issue with displaying columns when rows are empty. If you try
 *     to update fields while rows is an empty array, those fields will not get displayed. As a
 *     workaround, we use the afterViewInit flag to render the clarity datagrid, to allow the parent
 *     component to update its fields in its own ngAfterViewInit hook.
 *
 * @author alextsg, Aravindh Nagarajan, Zhiqian Liu
 */
@Component({
    selector: 'avi-data-grid-base',
    templateUrl: './avi-data-grid-base.component.html',
})
export class AviDataGridBaseComponent implements OnInit, AfterViewInit, OnChanges, DoCheck, OnDestroy {
    /**
     * Grid configuration object containing getUniqueRowId, multipleactions, etc.
     */
    @Input()
    public config: IAviDataGridConfig;

    /**
     * Data to be displayed by the grid for the current page.
     */
    @Input()
    public rows: IAviDataGridRow[] = [];

    /**
     * Total count of data to be displayed.
     */
    @Input()
    public rowsTotal: number;

    /**
     * Max number of items displayed at one time for a grid page with default size 10.
     */
    @Input()
    public pageSize ? = 10;

    /**
     * Set the current page for the paginated dataset.
     */
    @Input()
    public currentPage?: number;

    /**
     * Indicate whether the row data is being loaded.
     */
    @Input()
    public isLoading ? = false;

    /**
     * Flag for search.
     */
    @Input()
    public showSearch ? = false;

    /**
     * Set a default sorting for a data model which already has sorting functionality equipped.
     * This will cause the comparator function to return the default field id as a sortable property
     * string.
     * Internally used by avi-collection-data-grid only, when sortBy is missing.
     */
    @Input()
    public defaultSortingFieldId ?: string;

    /**
     * Selected rows passed down from the parent.
     * The selected rows can be decided in two ways:
     *     a) by user interaction: clicking the checkbox of each row
     *     b) by parent component: the rows are programatically selected and passed down, ususally
     *        used for highlighting purpose
     * Both ways update the internal property this.selected.
     */
    @Input('selectedRows')
    private set setSelectedRows(rows: IAviDataGridRow[] | undefined) {
        this.selected = rows;
    }

    /**
     * Fires on row selection change.
     */
    @Output()
    public onSelectionChange = new EventEmitter<IAviDataGridRow[]>();

    /**
     * Fires on Row order change.
     */
    @Output()
    public onRowOrderChange = new EventEmitter<CdkDragDrop<IAviDataGridRow>>();

    /**
     * Parent callback to update row data through API calls.
     * Called when states of the data grid changes (bound by ClrDatagridStateInterface.)
     * i.e., pagination, sorting or filtering changes will trigger the call.
     */
    @Output()
    public onDataGridStateChange = new EventEmitter<ClrDatagridStateInterface>();

    /**
     * Fires on search is confirmed by 'Enter' or emptied by the clear icon.
     */
    @Output()
    public onSearch = new EventEmitter<string>();

    @Output()
    public columnVisibilityHashChange = new EventEmitter<TAviDataGridColumnPresenceMap>();

    @ViewChild('searchInput')
    private searchInputRef: ElementRef;

    /**
     * Reference to the keydown event listener cleanup function.
     * Used to remove the keydown event listener when the component is destroyed.
     */
    private removeKeydownListener?: () => void;

    /**
     * Cache config.fields for implementing a custom change detection.
     */
    public fields: IAviDataGridConfigField[] = [];

    public readonly filterTooltipPositionsPriority: ConnectedPosition[] = [{
        originX: 'start',
        originY: 'bottom',
        overlayX: 'end',
        overlayY: 'top',
        offsetX: 15,
    }];

    public readonly filterTooltipVisibilitySubject$ = new Subject<boolean>();

    public filterTooltipVisibility = false;

    /**
     * Fields that are not hidden by column selection; the actual fields shown in the grid.
     */
    public filteredFields: IAviDataGridConfigField[] = [];

    /**
     * List of selected rows.
     */
    public selected: IAviDataGridRow[] | undefined;

    /**
     * Boolean set to true after the ngAfterViewInit hook has been called, used as a workaround to
     * fix Clarity datagrid issues.
     */
    public afterViewInit = false;

    /**
     * If true, displays the singleactions column.
     */
    public hasSingleactions = false;

    /**
     * Width of the singleactions column in pixels.
     */
    public readonly singleactionsWidth = 50;

    /**
     * Single action options passed to the AviDropdownButton component.
     */
    public singleactionDropdownOptions: IAviDropdownButtonAction[] = [];

    /**
     * Indicator to decide grid expansion. The grid is expanded by default if not configured.
     * The default expanding state can be configured by the layout object.
     */
    public isGridExpanded = true;

    /**
     * If true, show the expander icon for grid content.
     * The grid is not opted for shrinking by default.
     */
    public showGridExpander = false;

    /**
     * If true, show the column containing the expander icons.
     */
    public showExpanderColumn = false;

    /**
     * If true, show expander icons for rows.
     */
    public showRowExpanders = false;

    /**
     * Token used for search.
     */
    public searchValue: string;

    /**
     * If true, show the search bar, otherwise show search icon only.
     */
    public showSearchBar = false;

    /**
     * If true, show the footer.
     */
    public showFooter = true;

    /**
     * Map managed by avi-data-grid-column-selection component to toggle true/false for a field ID,
     * which shows/hides a corresponding field column.
     */
    @Input()
    public columnVisibilityHash: TAviDataGridColumnPresenceMap = {};

    /**
     * Width in pixels to be used for icon cells.
     */
    public readonly iconWidth = 40;

    /**
     * Cache for custom comparator instances to ensure consistent object references.
     * This prevents creating new comparator instances on every change detection cycle.
     */
    private readonly comparatorCache = new Map<string, ClrDatagridComparatorInterface<IAviDataGridRow>>();

    /**
     * Get keys from global source bundles for template usage.
     */
    public readonly globalL10nKeys = globalL10nKeys;

    /**
     * Get keys from source bundles for template usage
     */
    public readonly l10nKeys = l10nKeys;

    /**
     * Positions of the column tooltip with respect to column text.
     */
    public readonly tooltipPositions: [ConnectedPosition] = [TOP_LEFT_ALIGNED_CONNECTED_POSITION];

    /**
     * trackBy function for row data.
     */
    public trackByRowId: (index: number, row: IAviDataGridRow) => string | number | IAviDataGridRow;

    /**
     * Map for keeping track of which rows have been expanded, based on the rowId.
     */
    private readonly expandedRows = new Set<IAviDataGridRow>();

    /**
     * Subject to control the hide/show column tooltip.
     */
    private readonly columnTooltipControlSubject = new Subject<boolean>();

    /**
     * Return an observable to allow hide/show column tooltip to close itself through subscription.
     */
    public get columnTooltipControl$(): Observable<boolean> {
        return this.columnTooltipControlSubject.asObservable();
    }

    public constructor(
        private readonly l10nService: L10nService,
        private readonly elementRef: ElementRef,
    ) {
        l10nService.registerSourceBundles(dictionary);
    }

    /** @override */
    public ngOnInit(): void {
        this.search = debounce(this.search, 500);
        this.resetSelected();
    }

    /** @override */
    public ngAfterViewInit(): void {
        this.setDisplayProps();
        this.setTrackByRowId();

        this.afterViewInit = true;

        // Add capture phase listener to intercept arrow key events for input elements before Clarity handles them
        // Using native addEventListener with capture phase since Renderer2.listen doesn't support it
        const element = this.elementRef.nativeElement;

        element.addEventListener('keydown', this.handleInputKeydown, true); // true = capture phase

        // Store cleanup function
        this.removeKeydownListener = () => {
            element.removeEventListener('keydown', this.handleInputKeydown, true);
        };
    }

    /** @override */
    public ngOnDestroy(): void {
        // Clean up the event listener
        this.removeKeydownListener?.();
    }

    /** @override */
    public ngOnChanges(changes: SimpleChanges): void {
        const { config } = changes;

        if (config) {
            if (config.firstChange) {
                return;
            }

            // Clear comparator cache when config changes to ensure fresh comparators
            this.comparatorCache.clear();

            this.setDisplayProps();
            this.setTrackByRowId();
        }
    }

    /**
     * @override
     * Used to add a custom change detection for this.config.fields. This detection is needed
     * because Angular is not able to detect any changes of fields since it's not directly bound to
     * the template.
     */
    public ngDoCheck(): void {
        if (this.config?.fields !== this.fields) {
            this.fields = this.config?.fields || [];

            // Clear comparator cache when fields change to ensure fresh comparators
            this.comparatorCache.clear();

            this.setColumnVisibilityHash();
        }
    }

    /**
     * Whether search is supported or not.
     */
    public get hasSearch(): boolean {
        return this.showSearch && this.onSearch.observers.length > 0;
    }

    /**
     * Returns a list of multipleactions - actions to be performed on selected data.
     */
    public get multipleactions(): IAviDataGridMultipleaction[] {
        return this.config?.multipleactions || [];
    }

    /**
     * Returns either the configured layout or the default layout.
     */
    public get layout(): IAviDataGridLayout {
        return this.config?.layout || { ...defaultLayout };
    }

    /**
     * Getter for the placeholder of search input with a default value.
     */
    public get searchInputPlaceholder(): string {
        return this.layout.searchInputPlaceholder ||
            this.l10nService.getMessage(globalL10nKeys.searchLabel);
    }

    /**
     * Decide if selection of a row should be disabled due to constraints.
     */
    public isRowSelectable(row: IAviDataGridRow): boolean {
        const { config } = this;

        return config.rowSelectionDisabled ? !config.rowSelectionDisabled(row) : true;
    }

    /**
     * Decide if a row expander should be deactivated due to constraints.
     */
    public isRowExpanderDeactivated(row: IAviDataGridRow): boolean {
        return Boolean(this.config.expanderDisabled?.(row));
    }

    /**
     * Calls the action and clears selected rows.
     */
    public callMultipleaction(action: IAviDataGridMultipleaction): void {
        callAction(action, this.selected);
        this.resetSelected();
    }

    /**
     * When the grid state changes with latest info, update the stored data grid state and call to
     * update data accordingly.
     */
    public refresh(dataGridState: ClrDatagridStateInterface): void {
        this.onDataGridStateChange.emit(dataGridState);
        this.resetSelected();
    }

    /**
     * Calls a singleaction on a row.
     */
    public callSingleaction(action: IAviDataGridSingleaction, row: IAviDataGridRow): void {
        callAction(action, row);
        this.resetSelected();
    }

    /**
     * trackBy function for each field.
     */
    public trackByFieldId(index: number, field: IAviDataGridConfigField): string {
        return field?.id || '';
    }

    /**
     * Called to get the value of disabled callback function on sinlge-actions, if present.
     * Else do not disable actions.
     */
    public isSingleActionDeactivated(
        action: IAviDataGridSingleaction,
        row: IAviDataGridRow,
    ): boolean {
        if (action.disabled) {
            return action.disabled(row);
        } else {
            return false;
        }
    }

    /**
     * Called to get the value of hidden callback function on sinlge-actions, if present.
     * Else do not hide actions.
     */
    public isSingleActionHidden(action: IAviDataGridSingleaction, row: IAviDataGridRow): boolean {
        if (action.hidden) {
            return action.hidden(row);
        } else {
            return false;
        }
    }

    /**
     * Called to get the value of disabled callback function on multi-actions, if present.
     * Else do not disable actions.
     */
    public isMultipleActionDisabled(action: IAviDataGridMultipleaction): boolean {
        if (action.disabled) {
            return action.disabled(this.selected);
        } else {
            return false;
        }
    }

    /**
     * trackBy function for actions.
     */
    public trackByActionLabel(index: number, action: IAviDataGridMultipleaction): string {
        return action?.label || '';
    }

    /**
     * Returns a placeholder message to be displayed if there are no items to display.
     */
    public get placeholderMessage(): string {
        const { l10nService } = this;

        return this.layout.placeholderMessage ||
            l10nService.getMessage(l10nKeys.defaultEmptyListPlaceholderMessage);
    }

    /**
     * Hides Pagination for Grid when hidePagination is set "true" or
     * withReordering is set "true".
     */
    public get hidePagination(): boolean {
        const { hidePagination, withReordering } = this.config.layout || {};

        return Boolean(hidePagination) || Boolean(withReordering);
    }

    /**
     * Update columnVisibilityHash and filteredFields accordingly to reflect the latest shown fields.
     */
    public onColumnVisibilityHashChange(hash: TAviDataGridColumnPresenceMap): void {
        this.columnVisibilityHash = hash;
        this.filteredFields = this.fields.filter(({ id }) => this.columnVisibilityHash[id]);
        this.columnVisibilityHashChange.emit(this.columnVisibilityHash);
    }

    /**
     * Revert grid expansion state.
     */
    public toggleGridExpanded(): void {
        this.isGridExpanded = !this.isGridExpanded;
    }

    /**
     * Sets the expanded state on the expandedRows Map.
     */
    public toggleRowExpanded(row: IAviDataGridRow): void {
        const expanded = this.isRowExpanded(row);

        if (!expanded) {
            this.expandedRows.add(row);
        } else {
            this.expandedRows.delete(row);
        }
    }

    /**
     * Retrieves the expanded state from the expandedRows Map.
     */
    public isRowExpanded(row: IAviDataGridRow): boolean {
        return Boolean(this.expandedRows.has(row));
    }

    /**
     * Emits selected rows on selectionChange event.
     */
    public selectionChanged(selectedRows: IAviDataGridRow[]): void {
        // for two-way binding of this.selected.
        this.selected = selectedRows;

        this.onSelectionChange.emit(this.selected);
    }

    /**
     * Emits onRowOrderChange event to parent with CdkDragDrop event.
     */
    public emitRowOrderChange(event: CdkDragDrop<IAviDataGridRow>): void {
        this.onRowOrderChange.emit(event);
    }

    /*
     * Generate a comparator for sorting by a grid column field to conform with the type required by
     * [clrDgSortBy].
     * Designed so that both "property name string" and "function" sortings are available for
     * regular data grids as long as an implementation is added in a data-grid component based on
     * the DataGridState bubbled up from data-grid-base, while only "string" sorting is available
     * for a collection-data-grid since the implementation shouldn't contain a function comparator
     * interface according to its server-driven nature.
     */
    // eslint-disable-next-line class-methods-use-this
    public getSortingComparator(
        configField: IAviDataGridConfigField,
    ): string | ClrDatagridComparatorInterface<IAviDataGridRow> | undefined {
        const {
            id,
            sortBy,
        } = configField;

        if (sortBy) {
            if (isString(sortBy)) {
                return sortBy;
            } else {
                // Use cached comparator instance to ensure consistent object reference for Clarity to properly track
                // sort state. Without this, Clarity doesn't know whether to render the sorting icon as ascending or
                // descending.
                let cachedComparator = this.comparatorCache.get(id);

                if (!cachedComparator) {
                    cachedComparator = {
                        compare(a: IAviDataGridRow, b: IAviDataGridRow) {
                            return sortBy(a, b);
                        },
                    };

                    this.comparatorCache.set(id, cachedComparator);
                }

                return cachedComparator;
            }
        }

        // Add a default sorting option based on the field id, particularly used by collection data
        // grids
        const { defaultSortingFieldId } = this;

        if (defaultSortingFieldId && id === defaultSortingFieldId) {
            return id;
        }
    }

    /**
     * Filters collection, debouncing multiple calls
     */
    public search(): void {
        this.onSearch.emit(this.searchValue);
    }

    /**
     * Expand the search section with a search bar and a clear icon.
     */
    public expandSearchBar(): void {
        this.showSearchBar = true;

        setTimeout(() => {
            this.searchInputRef.nativeElement.focus();
        });
    }

    /**
     * Clear the search input field.
     */
    public clearSearch(): void {
        this.searchValue = undefined;
        this.showSearchBar = false;

        this.search();
    }

    /**
     * Formats field label as single string with hyphens.
     */
    public getFormattedFieldLabel(fieldLabel: string): string {
        return fieldLabel.replace(/ /g, '-');
    }

    public toggleFilterTooltip(): void {
        this.filterTooltipVisibilitySubject$.next(
            this.filterTooltipVisibility = !this.filterTooltipVisibility,
        );
    }

    /**
     * Get the class names to be used for the row element of the datagrid.
     */
    public getRowClassNames(row: IAviDataGridRow, index: number): string {
        const { rowClass } = this.config;
        const classNames = [
            `sel-avi-data-grid-row-${index}`,
        ];

        if (rowClass && typeof rowClass === 'function') {
            classNames.push(rowClass(row) ?? '');
        }

        return classNames.join(' ');
    }

    /**
     * Stop propagation of arrow key events when they originate from input elements within the data grid.
     * This prevents Clarity's cell navigation from interfering with normal input cursor movement.
     * Allows arrow keys to move the cursor within input fields instead of jumping between cells.
     * Uses capture phase to intercept events before Clarity's handlers can process them.
     */
    private handleInputKeydown = (event: KeyboardEvent): void => {
        const target = event.target as HTMLElement;

        // Check if the event originated from an input, textarea, or contenteditable element
        const isInputElement = target.tagName === 'INPUT' ||
            target.tagName === 'TEXTAREA' ||
            target.isContentEditable;

        // Stop propagation for left/right arrow keys when inside input elements
        // This prevents Clarity's data grid from handling these keys for cell navigation
        // Use stopImmediatePropagation to prevent all other handlers from executing
        if (isInputElement && (event.key === 'ArrowLeft' || event.key === 'ArrowRight')) {
            event.stopImmediatePropagation();
        }
    };

    /**
     * Reset row selection array to its default value.
     */
    private resetSelected(): void {
        this.selected = this.getDefaultSelected();
    }

    /**
     * Returns the default value to use for the selected property, which is used by clr-datagrid.
     * If undefined, the checkboxes will not be rendered.
     */
    private getDefaultSelected(): IAviDataGridRow[] | undefined {
        return this.layout.hideCheckboxes ? undefined : [];
    }

    /**
     * Sets datagrid display properties based on the config. Called in initialization as well as on
     * config changes.
     */
    private setDisplayProps(): void {
        if (!this.config) {
            return;
        }

        const {
            singleactions = [],
            expandedContentTemplateRef,
            isExpandedContentColumnBased,
            isGridExpandable,
        } = this.config;

        this.hasSingleactions = Boolean(singleactions.length);
        this.singleactionDropdownOptions = singleactions.map(action => {
            return {
                onClick: (row: IAviDataGridRow) => this.callSingleaction(action, row),
                hidden: (row: IAviDataGridRow) => this.isSingleActionHidden(action, row),
                disabled: (row: IAviDataGridRow) => this.isSingleActionDeactivated(action, row),
                label: action.label,
            };
        });

        this.showFooter = this.layout.showFooter === undefined ? true : this.layout.showFooter;

        this.showGridExpander = Boolean(isGridExpandable);

        if (this.showGridExpander) {
            this.isGridExpanded = Boolean(!this.layout.expandGridByDefault);
        }

        // grid and rows can't be expandable at the same time
        this.showRowExpanders = Boolean(
            expandedContentTemplateRef || isExpandedContentColumnBased,
        ) && !this.showGridExpander;
        this.showExpanderColumn = this.showGridExpander || this.showRowExpanders;

        this.setColumnVisibilityHash();
    }

    /**
     * Set columnVisibilityHash to show fields with visibility of MANDATORY, DEFAULT or no visibility set,
     * and to hide fields with visibility of OPTIONAL.
     */
    private setColumnVisibilityHash(): void {
        const { columnVisibilityHash } = this;

        if (Object.keys(columnVisibilityHash).length === 0) {
            this.fields.forEach(({ id, visibility }) => {
                columnVisibilityHash[id] = visibility !== AviDataGridFieldVisibility.OPTIONAL;
            });
        }

        this.onColumnVisibilityHashChange(columnVisibilityHash);
    }

    /**
     * Initialize trackByRowId function.
     * If getUniqueRowId doesn't exist, set trackBy to return row rather than index.
     * Returning index causes problems with the clarity datagrid.
     */
    private setTrackByRowId(): void {
        if (!this.config) {
            return;
        }

        if (this.config.getUniqueRowId) {
            this.trackByRowId = (index, row: IAviDataGridRow) => {
                return this.config.getUniqueRowId(row);
            };
        } else {
            this.trackByRowId = (index, row: IAviDataGridRow): IAviDataGridRow => {
                return row;
            };
        }
    }
}
